import FolderIcon from '@mui/icons-material/Folder'
import Grid from '@mui/material/Grid2'
import ImageListItem from '@mui/material/ImageListItem'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { includes, indexOf, map, some } from 'lodash'
import { FC, ReactNode } from 'react'
import { useSearchParams } from 'react-router-dom'
import { DocumentIconName } from '../../../../components/DocumentIconName'
import { FileIcon } from '../../../../components/FileIcon'
import { useFilesStore } from '../../../../store/storeFiles'
import { File, Folder } from '../../../../swagger/dtoGateway'
import { getFileType } from '../../../../utils/files'
import { FileItemMenu } from './FileItemMenu'
import { FileUploaderButton } from './FileUploaderButton'

const IMAGE_MAP = ['png', 'jpg', 'jpeg']

type Props = {
    files?: File[]
    folders?: Folder[]
    handleClick: VoidFunction
    idEntity?: string
    isReadOnly?: boolean
    fullWidth?: boolean
    withUpload?: boolean
    isShowButton?: boolean
    onRenderFileItemControls?: (file: File) => ReactNode
}

export const FileCardList: FC<Props> = ({
    files = [],
    folders = [],
    idEntity,
    isReadOnly,
    handleClick,
    fullWidth = false,
    withUpload = true,
    isShowButton = true,
    onRenderFileItemControls,
    isChoiceFilesDialog,
    choiceFiles,
    handleClickChoiceFiles,
}) => {
    const { fileUrlList } = useFilesStore((state) => state)
    const [searchParams, setSearchParams] = useSearchParams()

    const handleClickFolder = (name: string) => {
        const folder = decodeURI(name?.split('disk').pop() ?? '')
        searchParams.set('folder', folder)
        setSearchParams(searchParams)
    }
    const handleClickOpen = (file) => {
        if (isChoiceFilesDialog) {
            handleClickChoiceFiles(file)
        } else {
            window.open(file.url)
        }
    }

    return (
        <>
            {withUpload && !isReadOnly && isShowButton ? (
                <Grid
                    sx={{
                        borderRadius: '16px',
                        background: '#F9FAFB',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}
                    size={{ xs: 12, sm: 12, md: 6, lg: 3 }}>
                    <FileUploaderButton idEntity={idEntity} />
                </Grid>
            ) : null}
            {map([...folders, ...files], (file, index) => {
                const type = getFileType(file.url ?? '')
                const isFolder = file.object_type === 'folder'
                const isChoiceFiles = some(choiceFiles, (f) => f.url === file.url)

                return (
                    <Grid
                        onClick={() => isReadOnly && handleClick(file.url)}
                        size={!fullWidth ? { xs: 12, sm: 12, md: 6, lg: 3 } : 12}
                        sx={{
                            borderRadius: withUpload ? '16px' : '',
                            border: isChoiceFiles
                                ? '1px solid #006CFD'
                                : withUpload
                                  ? indexOf(fileUrlList, file.url) > -1
                                      ? '1px solid #004B9F'
                                      : '1px solid #E9EAED'
                                  : 'none',
                            margin: 0,
                            position: 'relative',
                            '&:hover #cellBoxLengthTypo': {
                                visibility: 'visible',
                            },
                            '& #cellBoxLengthTypo': { visibility: 'hidden' },
                            borderBottom: !withUpload ? '1px solid #E9EAED' : '',
                        }}
                        key={index}>
                        <Paper
                            className={withUpload ? 'Paper-primary' : ''}
                            sx={{
                                textAlign: 'left',
                                position: 'relative',
                                display: 'flex',
                                flexDirection: 'column',
                            }}>
                            {withUpload ? (
                                <div
                                    onClick={() =>
                                        isFolder
                                            ? handleClickFolder(file.filename)
                                            : !isReadOnly && handleClickOpen(file)
                                    }
                                    style={{
                                        cursor: 'pointer',
                                    }}>
                                    <Paper
                                        sx={{
                                            p: 0,
                                            cursor: 'pointer',
                                            borderRadius: '6px',
                                            background: '#fff',
                                            height: '104px',
                                            position: 'relative',
                                            display: 'flex',
                                            alignContent: 'center',
                                            justifyContent: 'center',
                                            flexWrap: 'wrap',
                                        }}>
                                        {includes(IMAGE_MAP, type) ? (
                                            <ImageListItem sx={{ height: '104px', width: '100%' }}>
                                                <img
                                                    style={{ height: '104px' }}
                                                    srcSet={`${file.url}?w=164&h=104`}
                                                    src={`${file.url}?w=164&h=104&fit=crop&auto=format`}
                                                    alt={file.title}
                                                    loading="lazy"
                                                />
                                            </ImageListItem>
                                        ) : file.object_type === 'file' ? (
                                            <FileIcon
                                                type={type}
                                                style={{
                                                    color: '#004B9F',
                                                    width: '42px',
                                                    height: '42px',
                                                }}
                                            />
                                        ) : (
                                            <FolderIcon
                                                sx={{ color: '#004B9F', fontSize: '42px' }}
                                            />
                                        )}
                                    </Paper>
                                </div>
                            ) : null}
                            {file.object_type === 'file' && (
                                <Grid
                                    sx={{
                                        overflow: 'hidden',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                    }}>
                                    <DocumentIconName
                                        name={file.filename}
                                        isWrapper={false}
                                        size={!withUpload && file.size}
                                        type={type}
                                    />
                                    <Stack direction="row" sx={{ gap: 1, alignItems: 'center' }}>
                                        {onRenderFileItemControls?.(file)}
                                        {!isReadOnly && !isChoiceFilesDialog && (
                                            <FileItemMenu file={file} idEntity={idEntity} />
                                        )}
                                    </Stack>
                                </Grid>
                            )}

                            {isFolder && (
                                <Stack
                                    direction="row"
                                    sx={{
                                        overflow: 'hidden',
                                        mt: 2,
                                        justifyContent: 'space-between',
                                    }}>
                                    <Stack
                                        direction="column"
                                        sx={{
                                            overflow: 'hidden',
                                        }}>
                                        <Typography
                                            variant="caption2"
                                            sx={{
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                            }}>
                                            {decodeURI(
                                                file.filename?.slice(0, -1).split('/').pop() ?? '',
                                            )}
                                        </Typography>
                                        <Typography
                                            variant="caption2"
                                            sx={{ color: '#5E626E', mt: 0 }}>
                                            {file.total_count} объектов
                                        </Typography>
                                    </Stack>

                                    {!isChoiceFilesDialog && (
                                        <FileItemMenu file={file} idEntity={idEntity} />
                                    )}
                                </Stack>
                            )}
                        </Paper>
                    </Grid>
                )
            })}
        </>
    )
}
